import React, { useState } from "react";
import TestActions from "./TestActions";
import { FaStar, FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from "react-router";
import { Card, CardContent } from '@/components/ui/card';
import { Stack } from '@/components/ui/layout';
import { Text } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const TestCard = ({ tests, setSelectedTestType }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [showPopup, setShowPopup] = useState(false);
  const [showPopup2, setShowPopup2] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(null);
  const parseISTDate = (scheduledTimeStr) => {
    const cleanedStr = scheduledTimeStr.replace('GMT', '').trim(); // if wrongly marked as GMT
    return new Date(cleanedStr);
  };
  const filteredTests = tests.filter((test) => {
    const scheduledTime = parseISTDate(test.scheduled_time);
    const now = new Date();
    const diffMinutes = (now - scheduledTime) / (1000 * 60);
    
    // Get user email from localStorage for expiration check
    const userEmail = localStorage.getItem("user_email");
    const isExpired = userEmail === "anubhav.mishra@newzenalpha.com" ? false : diffMinutes > 60;
    
    const isUpcoming = now < scheduledTime;

    if (filter === "all") return true;
    if (filter === "upcoming") return test.isCompleted === 0 && scheduledTime > now;
    if (filter === "expired") return test.isCompleted === 0 && isExpired;
    if (filter === "completed") return test.isCompleted === 1;
    return true;
  });

  const isTestExpired = (scheduledTimeStr) => {
    // Get user email from localStorage
    const userEmail = localStorage.getItem("user_email");
    
    // Special case: Never expire tests for anubhav.mishra@newzenalpha.com
    if (userEmail === "anubhav.mishra@newzenalpha.com") {
      return false;
    }
    
    const scheduledIST = parseISTDate(scheduledTimeStr);
    const now = new Date();
    const diffMinutes = (now - scheduledIST) / (1000 * 60);
    return diffMinutes > 60;
  };

  const isTestAvailableNow = (scheduledTimeStr) => {
    const scheduledIST = parseISTDate(scheduledTimeStr);
    const now = new Date();
    
    // Get user email from localStorage
    const userEmail = localStorage.getItem("user_email");
    
    // Special case: Always allow access for anubhav.mishra@newzenalpha.com if scheduled time has passed
    if (userEmail === "anubhav.mishra@newzenalpha.com") {
      return now >= scheduledIST;
    }
    
    return now >= scheduledIST && !isTestExpired(scheduledTimeStr);
  };

  const convertToIST = (gmtDate) => {
    // Create a Date object from the GMT date string
    const date = new Date(gmtDate);

    // Format the date to a beautified string (e.g., March 13, 2025, 12:46 PM)
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true, // To show AM/PM
      timeZone: 'UTC', // Ensure it stays in GMT
    };
    return date.toLocaleString('en-IN', options);
  };

  const getTimeRemainingMessage = (scheduledTimeStr) => {
    const scheduledIST = parseISTDate(scheduledTimeStr);
    const now = new Date();
    const diffMs = scheduledIST - now;

    if (diffMs <= 0) return;

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    if (hours > 0) {
      return `Starts in ${hours} hr${hours > 1 ? "s" : ""} ${minutes} min${minutes !== 1 ? "s" : ""}`;
    } else {
      return `Starts in ${minutes} min${minutes !== 1 ? "s" : ""}`;
    }
  };

  const handleSubmitRating = async () => {
    try {
      fetch('/submit-rating', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rating })
      });
      // Show the feedback popup
      setShowPopup2(true);
      setShowPopup(false);
      // After 3 seconds, navigate to /user-home
      setTimeout(() => {
        window.location.reload();
        setShowPopup2(false);
        setShowPopup(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting rating:', error);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    window.location.reload();
  };

  return (
    <div className={cn("w-full")}>
      <div className={cn("w-full")}>
        <div className={cn(
          "flex justify-between gap-2.5 items-center",
          "sticky top-0 z-10",
          "p-5 bg-background",
          "shadow-sm"
        )}>
          <TestActions setSelectedTestType={setSelectedTestType} />
        </div>

        <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-5")}>
          {filteredTests.length > 0 ? (
            filteredTests.map((test) => (
              <Card key={test.test_id} className={cn("shadow-md hover:shadow-lg transition-shadow")}>
                <CardContent className="p-6">
                  <Stack spacing="md">
                    <div className={cn("flex justify-between items-center")}>
                      <Text className="font-semibold">Company:</Text>
                      <Text variant="muted">{test.company_name}</Text>
                    </div>
                    <div className={cn("flex justify-between items-center")}>
                      <Text className="font-semibold">Position:</Text>
                      <Text variant="muted">{test.topic}</Text>
                    </div>
                    <div className={cn("flex justify-between items-center")}>
                      <Text className="font-semibold">Test Type:</Text>
                      <Text variant="muted">
                        {test.test_type === "c"
                          ? "Coding Test"
                          : test.test_type === "t"
                            ? "Technical Test"
                            : test.test_type === "p"
                              ? "Personality Test"
                              : test.test_type === "b"
                                ? "Behavioral Test"
                                : test.test_type === "a"
                                  ? "Personality & Behavioral Test"
                                  : "Unknown Test Type"}
                      </Text>
                    </div>
                    <div className={cn("flex flex-col gap-1")}>
                      <div className={cn("flex justify-between items-center")}>
                        <Text className="font-semibold">Test Time:</Text>
                        <Text variant="muted">{convertToIST(test.scheduled_time)}</Text>
                      </div>
                      {test.isCompleted === 0 && !isTestExpired(test.scheduled_time) && (
                        <Text variant="muted" className="text-xs">
                          {getTimeRemainingMessage(test.scheduled_time)}
                        </Text>
                      )}
                    </div>
                    <Button
                      onClick={() => navigate('/mock-result')}
                      className="w-full"
                    >
                      <img
                        src="https://cdn.builder.io/api/v1/image/assets/TEMP/96a4f8e7c6838dcfa75b9f1d88ec1b4960a02c6bf6b6f240bbc16f2a41b5dc77?apiKey=e90a33775c2844879a20708b8df7376f"
                        alt="Start"
                        className="w-5 h-5 mr-2"
                      />
                      View Result
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className={cn("col-span-full text-center py-8")}>
              <Text variant="muted">No Test Scheduled for this category.</Text>
            </div>
          )}
        </div>
      </div>
      
      <Dialog open={showPopup} onOpenChange={setShowPopup}>
        <DialogContent>
          <Stack spacing="md" align="center">
            <Text className="text-xl font-bold">✅ Test Submitted Successfully</Text>
            <div className={cn("w-full space-y-4")}>
              <Text>We'd love your feedback! How would you rate your experience?</Text>
              <div className={cn("flex justify-center gap-2")}>
                {[...Array(5)].map((_, index) => {
                  const starValue = index + 1;
                  return (
                    <FaStar
                      key={index}
                      size={30}
                      color={starValue <= (hover || rating) ? "#ffc107" : "#e4e5e9"}
                      onClick={() => setRating(starValue)}
                      onMouseEnter={() => setHover(starValue)}
                      onMouseLeave={() => setHover(null)}
                      className="cursor-pointer transition-colors"
                    />
                  );
                })}
              </div>
            </div>
            <Button onClick={handleSubmitRating} className="w-full">Rate Now</Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={showPopup2} onOpenChange={setShowPopup2}>
        <DialogContent>
          <Stack spacing="md" align="center" className="text-center">
            <FaCheckCircle color="#4BB543" size={50} />
            <Text className="text-xl font-bold">Thank You for Your Feedback!</Text>
            <Text variant="muted">
              We appreciate you taking the time to rate your interview experience.
            </Text>
          </Stack>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestCard;